// PostHog product analytics, shared project with the AFP frontend.
//
// PostHog is only initialised once the CMP (or Bobbie's host page) reports
// consent — see consent.js. Before that the SDK is not loaded at all, so no
// stored opt-in from an earlier visit can capture anything and no PostHog
// request leaves the browser. Users are identified by their AFP user id only,
// the same id airteam.cloud uses, so a person is one profile across both apps.

import posthog from "posthog-js";
import { sanitizePostHogEvent } from "@/utils/posthog-sanitize";

// Only production's config.js (cloudbuild.yaml, main) sets these.
const token = () => window.runtimeConfig?.POSTHOG_PROJECT_TOKEN;
const host = () => window.runtimeConfig?.POSTHOG_HOST;

export const isPostHogConfigured = () => Boolean(token() && host());

let isInitialized = false;

function initPostHog() {
  if (isInitialized) return;
  isInitialized = true;
  posthog.init(token(), {
    api_host: host(),
    // Withdrawal turns capturing off again; until then nothing is stored.
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    // Our feature flags come from the backend. PostHog's /flags request would
    // send the anonymous id on every reset, so turn it off.
    // Also keeps PostHog's remote config off, which posthog-sanitize.js relies
    // on: remote config can switch $elements off (elementsChainAsString), and
    // without $elements the rebuilt $elements_chain is dropped — autocapture
    // would lose its element data. Don't turn this off without revisiting that.
    advanced_disable_flags: true,
    // Single-page app: count a page view on every route change, not only on
    // the first load (URLs are sanitized in before_send).
    capture_pageview: "history_change",
    // Autocapture must not record element text (e.g. project addresses).
    mask_all_text: true,
    // No autocapture (team decision): only page views, page leaves, errors
    // and explicit events. This also turns off rage clicks. The allowlist in
    // posthog-sanitize.js stays as a safety net should autocapture return.
    autocapture: false,
    // URLs can carry share/Bobbie tokens and login_hint emails: strip them.
    before_send: sanitizePostHogEvent,
    disable_session_recording: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });
}

export function hasPostHogConsent() {
  return isInitialized && posthog.has_opted_in_capturing();
}

// The viewer learns who the user is only after it loaded the project, which
// can be before or after consent arrives — remember them for either order.
let currentUser = null;

function identifyCurrentUser() {
  if (!currentUser || !hasPostHogConsent()) return;
  const distinctId = String(currentUser.userId);

  if (posthog.get_distinct_id() !== distinctId) {
    const persistedUserId = posthog.get_property("$user_id");
    if (persistedUserId && String(persistedUserId) !== distinctId) {
      resetPostHogIdentity();
    }
  }

  // After any reset (it clears super properties): sent with every event so
  // internal traffic can be filtered out in PostHog (is_internal = true).
  posthog.register({ is_internal: currentUser.isEmployee });

  // Only the user id: keeps PostHog data pseudonymous (no email/name).
  if (posthog.get_distinct_id() !== distinctId) posthog.identify(distinctId);

  // Account-level analytics (AI Designer beta, sales funnel per company): the
  // user's company as a PostHog group — account id as key, name as property.
  // Same group as on airteam.cloud.
  const { company } = currentUser;
  if (company?.id !== null && company?.id !== undefined) {
    posthog.group("company", String(company.id), { name: company.name });
  }
}

// company: { id, name } of the user's company, if any.
export function identifyPostHogUser(userId, isEmployee, company = null) {
  if (userId === null || userId === undefined) return;
  currentUser = { userId, isEmployee: Boolean(isEmployee), company };
  identifyCurrentUser();
}

// posthog.reset() also clears PostHog's stored consent, and with capturing
// opted out by default that silently turns PostHog off until the next page load.
// Consent comes from the CMP for this browser, not from the user, so restore it.
export function resetPostHogIdentity() {
  if (!isInitialized) return;
  const wasOptedIn = posthog.has_opted_in_capturing();
  posthog.reset();
  if (wasOptedIn) posthog.opt_in_capturing({ captureEventName: null });
}

// On logout: forget the user so the next one on this browser starts fresh.
export function forgetPostHogUser() {
  currentUser = null;
  resetPostHogIdentity();
}

export function applyPostHogConsent(granted) {
  if (!isPostHogConfigured()) return;

  if (granted) {
    initPostHog();
    if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing();
    identifyCurrentUser();
  } else if (isInitialized && posthog.has_opted_in_capturing()) {
    // Withdrawn: stops capturing and removes PostHog's cookie/localStorage.
    posthog.opt_out_capturing();
  }
}
